import { splitEncoding } from '@contentful/live-preview';
import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import { useRouter } from 'next/router';

import BlogTableOfContents from '@/components/BlogComponents/BlogTableOfContents/BlogTableOfContents';
import Eyebrow from '@/components/Eyebrow/Eyebrow';
import Icon from '@/components/Icon/Icon';
import Link from '@/components/Link/Link';
import type { SocialSites } from '@/contentful/types';
import useGlobalStyles from '@/styles/global.styles';
import { generateSocialLinkShare } from '@/utils/blog';

import useStyles from './BlogInfo.styles';

interface BlogInfoContent {
  publicationDate: string;
  dateLabel?: string;
  author?: string;
  title?: string;
  socialShare: SocialSites[];
  blogBody?: RichTextDocument;
  showTableOfContents: boolean;
}

export default function BlogInfo({
  author,
  publicationDate,
  dateLabel = 'Published',
  socialShare,
  blogBody,
  title,
  showTableOfContents,
}: BlogInfoContent): React.JSX.Element {
  const router = useRouter();
  const { classes, cx } = useStyles();
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });

  const date = new Date(publicationDate);
  const formattedDate = date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });

  const url = `www.sunrun.com${router.asPath}`;
  return (
    <div className={cx(classes.blogInfo, globalClasses.body2)}>
      <div className={classes.publishingInfoWrapper}>
        <div className={classes.rowWrapper}>
          <div>
            {dateLabel} {formattedDate}
          </div>
          {author && <div>{author}</div>}
        </div>
        {socialShare && (
          <div className={classes.rowWrapper}>
            <Eyebrow text='SHARE TO SOCIAL MEDIA' isOnDark={false} />
            <div className={classes.socialIcons}>
              {socialShare.map((link) => {
                const cleanTitle = splitEncoding(title || '').cleaned;
                const cleanLink = splitEncoding(link).cleaned as SocialSites;

                return (
                  <Link
                    href={generateSocialLinkShare(cleanLink, url, cleanTitle)}
                    key={cleanLink}
                    aria-label={cleanLink}
                  >
                    <Icon name={cleanLink} />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {blogBody && showTableOfContents && (
        <div className={classes.tableOfContentsWrapper}>
          <BlogTableOfContents body={blogBody} />
        </div>
      )}
    </div>
  );
}
